import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getTabForPath, getPathForTab } from '@/config/routes';
import {
  Megaphone,
  Sparkles,
  User,
  CalendarDays,
  Play,
  ImageIcon,
  Trophy,
  FileArchive,
  Share2,
  Tags,
  X,
  LayoutDashboard,
  Layers,
  Mail,
  Settings,
  Star,
  Quote,
  Handshake,
  UsersRound,
  BriefcaseBusiness,
} from 'lucide-react';

interface ContentItem {
  key: string;
  label: string;
  icon: typeof Megaphone;
  managementOnly?: boolean;
}

const contentSectionItems: ContentItem[] = [
  { key: 'banner', label: 'Promo Banner', icon: Megaphone },
  { key: 'heroSection', label: 'Hero Section', icon: Sparkles },
  { key: 'aboutSection', label: 'About', icon: User },
  { key: 'toursEvents', label: 'Tours & Events', icon: CalendarDays },
  { key: 'music', label: 'Showcase', icon: Play },
  { key: 'image', label: 'Gallery', icon: ImageIcon },
  { key: 'awardsSection', label: 'Awards', icon: Trophy },
  { key: 'infoKit', label: 'Info Kit', icon: FileArchive },
  // Management-only section managers — a management website shows the FULL
  // artist section set plus these five.
  { key: 'services', label: 'Services', icon: BriefcaseBusiness, managementOnly: true },
  { key: 'artistRoster', label: 'Roster', icon: Star, managementOnly: true },
  { key: 'testimonials', label: 'Testimonials', icon: Quote, managementOnly: true },
  { key: 'clients', label: 'Clients', icon: Handshake, managementOnly: true },
  { key: 'team', label: 'Team', icon: UsersRound, managementOnly: true },
  { key: 'platforms', label: 'Platforms', icon: Share2 },
  { key: 'category', label: 'Categories', icon: Tags },
];

interface BottomNavProps {
  selectedDomain: string;
  /** Site type of the selected website — scopes the Content flyout items. */
  siteType?: 'artist' | 'management';
}

export default function BottomNav({ selectedDomain, siteType = 'artist' }: BottomNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = getTabForPath(location.pathname);
  const [contentOpen, setContentOpen] = useState(false);
  const flyoutRef = useRef<HTMLDivElement>(null);

  const visibleContentItems = contentSectionItems.filter((item) =>
    siteType === 'management' || !item.managementOnly,
  );
  const contentSectionKeys = visibleContentItems.map(i => i.key);

  // Close flyout on outside click
  useEffect(() => {
    if (!contentOpen) return;
    const handler = (e: MouseEvent) => {
      if (flyoutRef.current && !flyoutRef.current.contains(e.target as Node)) {
        setContentOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [contentOpen]);

  // Close flyout on route change
  useEffect(() => {
    setContentOpen(false);
  }, [location.pathname]);

  if (!selectedDomain) return null;

  const isContentActive = contentSectionKeys.includes(currentTab);

  const navItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      isActive: currentTab === 'dashboard',
      icon: LayoutDashboard,
      onClick: () => navigate(getPathForTab('dashboard')),
    },
    {
      key: 'content',
      label: 'Content',
      isActive: isContentActive,
      icon: Layers,
      onClick: () => setContentOpen(prev => !prev),
    },
    {
      key: 'contactWebsite',
      label: 'Leads',
      isActive: currentTab === 'contactWebsite',
      icon: Mail,
      onClick: () => navigate(getPathForTab('contactWebsite')),
    },
    {
      key: 'siteBranding',
      label: 'Settings',
      isActive: currentTab === 'siteBranding',
      icon: Settings,
      onClick: () => navigate(getPathForTab('siteBranding')),
    },
  ];

  return (
    <>
      {/* Flyout backdrop */}
      {contentOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden transition-opacity duration-200"
          onClick={() => setContentOpen(false)}
        />
      )}

      {/* Content flyout menu */}
      {contentOpen && (
        <div
          ref={flyoutRef}
          className="fixed bottom-[calc(3.5rem+env(safe-area-inset-bottom,0px)+0.5rem)] left-3 right-3 z-50 bg-card border border-border rounded-2xl shadow-2xl lg:hidden animate-in slide-in-from-bottom-4 fade-in duration-200"
          style={{ maxHeight: 'calc(100dvh - 8rem)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-4 pb-3">
            <div>
              <p className="text-sm font-semibold text-foreground">Content Sections</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Manage your website content</p>
            </div>
            <button
              onClick={() => setContentOpen(false)}
              className="p-1.5 rounded-full hover:bg-muted transition-colors text-muted-foreground"
            >
              <X size={18} />
            </button>
          </div>

          {/* Items grid */}
          <div className="px-3 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(100dvh - 13rem)' }}>
            <div className="grid grid-cols-3 gap-2">
              {visibleContentItems.map(item => {
                const Icon = item.icon;
                const isActive = currentTab === item.key;
                return (
                  <button
                    key={item.key}
                    onClick={() => {
                      navigate(getPathForTab(item.key));
                      setContentOpen(false);
                    }}
                    className={`group relative flex flex-col items-center gap-2 rounded-xl px-2 py-3.5 transition-all duration-150 ${
                      isActive
                        ? 'bg-sidebar-accent'
                        : 'hover:bg-muted/70 active:scale-95'
                    }`}
                  >
                    <div className={`flex items-center justify-center w-10 h-10 rounded-xl transition-colors ${
                      isActive ? 'bg-sidebar-accent text-primary' : 'text-muted-foreground'
                    }`}>
                      <Icon size={20} strokeWidth={isActive ? 2.2 : 1.8} />
                    </div>
                    <span className={`text-[11px] leading-tight font-semibold text-center ${
                      isActive ? 'text-primary' : 'text-foreground/80'
                    }`}>
                      {item.label}
                    </span>
                    {isActive && (
                      <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-primary" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Bottom nav bar */}
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 backdrop-blur-md lg:hidden">
        <div className="flex items-stretch justify-around">
          {navItems.map((item) => {
            const active = item.isActive || (item.key === 'content' && contentOpen);
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                onClick={item.onClick}
                className={`relative flex flex-1 flex-col items-center gap-0.5 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 text-[11px] font-medium transition-colors duration-200 ${
                  active
                    ? 'text-primary'
                    : 'text-muted-foreground active:text-foreground'
                }`}
              >
                <div className={`flex items-center justify-center w-12 h-7 rounded-full transition-colors duration-200 ${
                  active ? 'bg-primary/12' : ''
                }`}>
                  <Icon size={20} strokeWidth={active ? 2.4 : 1.6} />
                </div>
                <span className={active ? 'font-semibold' : ''}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}
